import { Game, useDeleteGameMutation, useGetGamesQuery } from '../../../gql-types';
import { M_GAME_NAME, ZDAR_ESCAPE_GAME_NAME, ZDAR_KIDS_GAME_NAME } from '../../../configuration/gamesConfiguration';
import { Modal } from 'react-bootstrap';
import { ProfileContext } from '../../../contexts/ProfileContext';
import { toast } from 'react-toastify';
import { useAdminListQuery } from '../../../hooks/useAdminListQuery';
import { useContext, useEffect, useState } from 'react';
import { useDebounced } from '../../../hooks/useDebounced';
import { useHistory } from 'react-router-dom';
import AdminGamesTable from '../../../components/common/admin/AdminGamesTable';
import Button from '../../../components/common/buttons/Button';

const GAME_TYPE_LABEL: Record<string, string> = {
    [ZDAR_ESCAPE_GAME_NAME]: 'Žďárska',
    [M_GAME_NAME]: 'Majky',
    [ZDAR_KIDS_GAME_NAME]: 'Dětská',
};

export default function AdminGamesList() {
    const history = useHistory();
    const { token, clearProfile } = useContext(ProfileContext);
    const filters = useAdminListQuery();
    const [localSearch, setLocalSearch] = useState(filters.q);
    const debouncedSearch = useDebounced(localSearch, 300);
    const [gameToDelete, setGameToDelete] = useState<Game | null>(null);

    useEffect(() => {
        if (debouncedSearch !== filters.q) {
            filters.update({ q: debouncedSearch }, { resetPage: true });
        }
    }, [debouncedSearch]); // eslint-disable-line

    const { data, previousData, loading, error, refetch } = useGetGamesQuery({
        variables: {
            jwt: token || '',
            gameType: filters.type || undefined,
            showFinished: filters.finished,
            showExpired: filters.expired,
            search: filters.q || undefined,
            limit: filters.pageSize,
            offset: (filters.page - 1) * filters.pageSize,
        },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    });

    const [deleteGame] = useDeleteGameMutation();

    useEffect(() => {
        if (error) {
            console.log(error);
            history.push('/error');
        }
    }, [error]); // eslint-disable-line

    const page = data?.games ?? previousData?.games;
    const matchedCount = page?.matchedCount ?? 0;
    const pageCount = Math.max(1, Math.ceil(matchedCount / filters.pageSize));

    const onReload = () => refetch();
    const onAdd = () => history.push('/admin/games/new');
    const onEdit = (game: Game) => history.push(`/admin/games/${game._id}/edit`);
    const onLogout = () => {
        clearProfile();
        history.push('/');
    };
    const onLeaderboard = () => window.open(window.location.origin + '/leaderboard', '_blank');

    const onCopyCode = async (code: string) => {
        try {
            await navigator.clipboard.writeText(code);
            toast('Kód zkopírován.', { type: 'success', autoClose: 1500 });
        } catch {
            toast('Kopírování se nezdařilo.', { type: 'error' });
        }
    };

    const onConfirmDelete = () => {
        if (!gameToDelete?._id) return;
        deleteGame({ variables: { _id: gameToDelete._id, jwt: token || '' } })
            .then(() => {
                toast('Hra smazána.', { type: 'success' });
                setGameToDelete(null);
                refetch();
            })
            .catch((err) => {
                console.log(err);
                toast('Chyba - hra nesmazána.', { type: 'error' });
                setGameToDelete(null);
            });
    };

    return (
        <div className="admin-page">
            <Modal show={!!gameToDelete} onHide={() => setGameToDelete(null)}>
                <Modal.Header closeButton>
                    <Modal.Title>Mazání</Modal.Title>
                </Modal.Header>
                <Modal.Body>Skutečně chcete smazat hru s kódem {gameToDelete?.code} ?</Modal.Body>
                <Modal.Footer>
                    <Button className="secondary-button" onClick={onConfirmDelete}>
                        Smazat
                    </Button>
                    <Button className="secondary-button" onClick={() => setGameToDelete(null)}>
                        Storno
                    </Button>
                </Modal.Footer>
            </Modal>

            <div className="admin-version">Verze: {process.env.REACT_APP_VERSION}</div>

            <div className="admin-top-buttons">
                <Button className="secondary-button" onClick={onAdd}>
                    Přidat hru
                </Button>
                <div className="admin-top-right">
                    <Button className="secondary-button" onClick={onLeaderboard}>
                        Pořadí týmů
                    </Button>
                    <Button className="secondary-button" onClick={onReload}>
                        Aktualizovat data
                    </Button>
                    <Button className="secondary-button" onClick={onLogout}>
                        Odhlásit
                    </Button>
                </div>
            </div>

            <div className="admin-stats-strip">
                <StatCard label="Celkem" value={page?.total} />
                <StatCard label="Aktivní" value={page?.totalActive} tone="active" />
                <StatCard label="Odehrané" value={page?.totalFinished} tone="finished" />
                <StatCard label="Expirované" value={page?.totalExpired} tone="expired" />
            </div>

            <div className="admin-toolbar">
                <div className="admin-search">
                    <svg
                        className="admin-search__icon"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <circle cx="11" cy="11" r="7" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        className="admin-input"
                        type="search"
                        name="admin-games-search"
                        value={localSearch}
                        onChange={(e) => setLocalSearch(e.target.value)}
                        placeholder="Hledat podle kódu, emailu, týmu nebo jména…"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                    />
                    {localSearch && (
                        <button
                            type="button"
                            className="admin-search__clear"
                            onClick={() => setLocalSearch('')}
                            aria-label="Vymazat vyhledávání"
                        >
                            ×
                        </button>
                    )}
                </div>
                <select
                    className="admin-select"
                    value={filters.type}
                    onChange={(e) => filters.update({ type: e.target.value }, { resetPage: true })}
                >
                    <option value="">Všechny typy</option>
                    <option value={ZDAR_ESCAPE_GAME_NAME}>{GAME_TYPE_LABEL[ZDAR_ESCAPE_GAME_NAME]}</option>
                    <option value={ZDAR_KIDS_GAME_NAME}>{GAME_TYPE_LABEL[ZDAR_KIDS_GAME_NAME]}</option>
                    <option value={M_GAME_NAME}>{GAME_TYPE_LABEL[M_GAME_NAME]}</option>
                </select>
                <Toggle
                    id="t-finished"
                    label="Odehrané"
                    checked={filters.finished}
                    onChange={(v) => filters.update({ finished: v }, { resetPage: true })}
                />
                <Toggle
                    id="t-expired"
                    label="Expirované"
                    checked={filters.expired}
                    onChange={(v) => filters.update({ expired: v }, { resetPage: true })}
                />
                {!filters.isDefault && (
                    <button
                        type="button"
                        className="admin-reset"
                        onClick={() => {
                            filters.reset();
                            setLocalSearch('');
                        }}
                    >
                        Reset filtrů
                    </button>
                )}
            </div>

            <AdminGamesTable
                items={(page?.items ?? []).filter((g): g is Game => g !== null)}
                loading={loading}
                pageSize={filters.pageSize}
                onEdit={onEdit}
                onDelete={(g) => setGameToDelete(g)}
                onCopyCode={onCopyCode}
                gameTypeLabel={GAME_TYPE_LABEL}
            />

            <div className="admin-pagination">
                <div className="admin-pagination__info">
                    {matchedCount === 0
                        ? 'Žádné výsledky'
                        : `${(filters.page - 1) * filters.pageSize + 1}–${Math.min(
                              filters.page * filters.pageSize,
                              matchedCount,
                          )} z ${matchedCount}`}
                </div>
                <div className="admin-pagination__controls">
                    <Button
                        className="secondary-button"
                        onClick={() => filters.update({ page: 1 })}
                        disabled={filters.page <= 1}
                    >
                        «
                    </Button>
                    <Button
                        className="secondary-button"
                        onClick={() => filters.update({ page: Math.max(1, filters.page - 1) })}
                        disabled={filters.page <= 1}
                    >
                        ‹
                    </Button>
                    <span className="admin-pagination__page">
                        {filters.page} / {pageCount}
                    </span>
                    <Button
                        className="secondary-button"
                        onClick={() => filters.update({ page: Math.min(pageCount, filters.page + 1) })}
                        disabled={filters.page >= pageCount}
                    >
                        ›
                    </Button>
                    <Button
                        className="secondary-button"
                        onClick={() => filters.update({ page: pageCount })}
                        disabled={filters.page >= pageCount}
                    >
                        »
                    </Button>
                    <select
                        value={filters.pageSize}
                        onChange={(e) =>
                            filters.update({ pageSize: parseInt(e.target.value, 10) }, { resetPage: true })
                        }
                    >
                        {[10, 20, 50, 100].map((n) => (
                            <option key={n} value={n}>
                                {n} / str.
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
}

type StatCardProps = { label: string; value?: number; tone?: 'active' | 'finished' | 'expired' };
function StatCard({ label, value, tone }: StatCardProps) {
    return (
        <div className={`stat-card${tone ? ` stat-card--${tone}` : ''}`}>
            <div className="stat-card__value">{value ?? '—'}</div>
            <div className="stat-card__label">{label}</div>
        </div>
    );
}

type ToggleProps = { label: string; id: string; checked: boolean; onChange: (v: boolean) => void };
function Toggle({ label, id, checked, onChange }: ToggleProps) {
    return (
        <label className={`toggle${checked ? ' toggle--on' : ''}`} htmlFor={id}>
            <input type="checkbox" id={id} name={id} checked={checked} onChange={(e) => onChange(e.target.checked)} />
            <span className="toggle__track">
                <span className="toggle__thumb" />
            </span>
            <span className="toggle__label">{label}</span>
        </label>
    );
}
